import { cn } from "@/lib/utils";
import type { SupportedLanguage } from "@/i18n";

interface FlagIconProps {
  lang: SupportedLanguage;
  className?: string;
  title?: string;
}

/**
 * Inline SVG flags so they render consistently across all OSes
 * (Windows does not ship regional-indicator emoji glyphs).
 */
export function FlagIcon({ lang, className, title }: FlagIconProps) {
  const baseClass = cn("inline-block rounded-sm overflow-hidden shadow-sm", className);
  const commonProps = {
    className: baseClass,
    role: "img" as const,
    "aria-label": title ?? lang.toUpperCase(),
    viewBox: "0 0 60 42",
    preserveAspectRatio: "xMidYMid slice" as const,
  };

  if (lang === "pt") {
    // Brazil
    return (
      <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="42" fill="#009b3a" />
        <polygon points="30,4 56,21 30,38 4,21" fill="#fedf00" />
        <circle cx="30" cy="21" r="8" fill="#002776" />
        <path
          d="M22.5 21.8 a8 8 0 0 1 15 -1.4"
          fill="none"
          stroke="#fff"
          strokeWidth="1.4"
        />
      </svg>
    );
  }

  if (lang === "en") {
    // United States (simplified)
    return (
      <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="42" fill="#fff" />
        {[0, 2, 4, 6, 8, 10, 12].map((i) => (
          <rect key={i} y={i * 3} width="60" height="3" fill="#b22234" />
        ))}
        <rect width="24" height="21" fill="#3c3b6e" />
      </svg>
    );
  }

  // Spain (simplified)
  return (
    <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
      <rect width="60" height="42" fill="#aa151b" />
      <rect y="10.5" width="60" height="21" fill="#f1bf00" />
    </svg>
  );
}